export type LanguageCode = 'en' | 'es' | 'fr' | 'it' | 'ru' | 'de' | 'pt';

export interface LanguageOption {
  code: LanguageCode;
  label: string;
  nativeName: string;
  flag: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', nativeName: 'English', flag: 'GB' },
  { code: 'es', label: 'Spanish', nativeName: 'Espanol', flag: 'ES' },
  { code: 'fr', label: 'French', nativeName: 'Francais', flag: 'FR' },
  { code: 'it', label: 'Italian', nativeName: 'Italiano', flag: 'IT' },
  { code: 'ru', label: 'Russian', nativeName: 'Русский', flag: 'RU' },
  { code: 'de', label: 'German', nativeName: 'Deutsch', flag: 'DE' },
  { code: 'pt', label: 'Portuguese', nativeName: 'Portugues', flag: 'PT' },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

export const LANGUAGE_CODES = LANGUAGES.map((l) => l.code);

const STORAGE_KEY = 'impresstars-lang';

export function getLanguageFromPath(pathname: string): LanguageCode {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];
  if (first && LANGUAGE_CODES.includes(first as LanguageCode)) {
    return first as LanguageCode;
  }
  return DEFAULT_LANGUAGE;
}

export function stripLanguageFromPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && LANGUAGE_CODES.includes(segments[0] as LanguageCode)) {
    return '/' + segments.slice(1).join('/');
  }
  return pathname;
}

export function buildLocalizedPath(path: string, lang: LanguageCode): string {
  const stripped = stripLanguageFromPath(path);
  if (lang === DEFAULT_LANGUAGE) {
    return stripped === '/' ? '/' : stripped;
  }
  return `/${lang}${stripped === '/' ? '' : stripped}`;
}

export function getStoredLanguage(): LanguageCode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LANGUAGE_CODES.includes(stored as LanguageCode)) {
      return stored as LanguageCode;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

export function storeLanguage(lang: LanguageCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // localStorage not available
  }
}

export function detectBrowserLanguage(): LanguageCode | null {
  if (typeof navigator === 'undefined') return null;
  const browserLang = navigator.language?.toLowerCase() ?? '';
  for (const code of LANGUAGE_CODES) {
    if (browserLang === code || browserLang.startsWith(code + '-')) {
      return code;
    }
  }
  return null;
}
